import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Text } from '@teals/ui-web';
import {
  useSendMessageMutation,
  useUpdateAgentConfigMutation,
  usePublishAgentMutation,
  type Agent,
  type AgentDetail,
} from '../../store/api';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import {
  ConfigureFleetPanel,
  ConfigureMembershipPanel,
  isManagementAgent,
} from './AgentSystemPanels';

const BUILDER_AGENT = 'agent-builder';

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface ProposedConfig {
  systemPrompt?: string;
  model?: string;
  temperature?: number;
  fleet?: string[];
  agentType?: string;
  orchestrationMode?: string;
}

interface BuilderPayload {
  stage?: string;
  message?: string;
  suggestions?: string[];
  agentConfig?: Record<string, unknown>;
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 520px;

  @media (max-width: 1023px) {
    min-height: min(520px, 50dvh);
  }
`;

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 420px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);

  @media (max-width: 1023px) {
    height: min(420px, 55dvh);
  }
`;

const ChatHeader = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
`;

const ChatTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChatSubtitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 90%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-primary)')};
  white-space: pre-wrap;
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  background: var(--bg-elevated, rgba(148, 163, 184, 0.12));
  color: var(--text-secondary);
  font-size: 0.8125rem;
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const ChatInput = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--text-primary);
  background: transparent;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: var(--accent);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DiffCard = styled.div`
  padding: 0.875rem;
  border: 1px solid var(--warning, #d97706);
  border-radius: 8px;
  background: color-mix(in srgb, var(--warning, #d97706) 8%, transparent);
  font-size: 0.8125rem;
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1023px) {
    position: static;
  }
`;

const BlockHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.375rem 0;
  font-size: 0.8125rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FieldName = styled.span`
  color: var(--text-muted);
`;

const FieldValue = styled.span<{ $changed?: boolean }>`
  color: ${({ $changed }) =>
    $changed ? 'var(--accent)' : 'var(--text-primary)'};
  font-weight: 500;
  text-align: right;
  max-width: 65%;
  word-break: break-word;
`;

const PromptPreview = styled.pre`
  margin: 0;
  max-height: 140px;
  overflow: auto;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--bg-elevated, #f8fafc);
  border: 1px solid var(--border);
  font-size: 0.75rem;
  white-space: pre-wrap;
  color: var(--text-secondary);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const ManualToggle = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  align-self: flex-start;
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

function parseBuilderResponse(raw: string): BuilderPayload {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { message: '' };

  const tryParse = (text: string): BuilderPayload | null => {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      return {
        stage: parsed.stage ? String(parsed.stage) : undefined,
        message: parsed.message
          ? String(parsed.message)
          : typeof parsed === 'object'
            ? undefined
            : text,
        suggestions: Array.isArray(parsed.suggestions)
          ? parsed.suggestions.map((s) => String(s))
          : undefined,
        agentConfig:
          parsed.agent_config && typeof parsed.agent_config === 'object'
            ? (parsed.agent_config as Record<string, unknown>)
            : parsed.agentConfig && typeof parsed.agentConfig === 'object'
              ? (parsed.agentConfig as Record<string, unknown>)
              : undefined,
      };
    } catch {
      return null;
    }
  };

  const direct = tryParse(trimmed);
  if (direct) {
    return {
      ...direct,
      message: direct.message || direct.agentConfig ? direct.message : trimmed,
    };
  }

  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence?.[1]) {
    const fromFence = tryParse(fence[1].trim());
    if (fromFence) {
      return {
        ...fromFence,
        message:
          fromFence.message ||
          trimmed.replace(fence[0], '').trim() ||
          fromFence.message,
      };
    }
  }

  const brace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (brace >= 0 && lastBrace > brace) {
    const fromSlice = tryParse(trimmed.slice(brace, lastBrace + 1));
    if (fromSlice) {
      return {
        ...fromSlice,
        message:
          fromSlice.message ||
          trimmed.slice(0, brace).trim() ||
          fromSlice.message,
      };
    }
  }

  return { message: trimmed };
}

function toProposed(agentConfig: Record<string, unknown>): ProposedConfig {
  const settings =
    agentConfig.settings && typeof agentConfig.settings === 'object'
      ? (agentConfig.settings as Record<string, unknown>)
      : {};
  const fleet =
    agentConfig.fleet && typeof agentConfig.fleet === 'object'
      ? (agentConfig.fleet as Record<string, unknown>)
      : {};
  const orch =
    agentConfig.orchestration && typeof agentConfig.orchestration === 'object'
      ? (agentConfig.orchestration as Record<string, unknown>)
      : {};
  const fleetAgents = Array.isArray(fleet.agents)
    ? fleet.agents.map((a) => String(a))
    : undefined;

  return {
    systemPrompt: agentConfig.system_prompt
      ? String(agentConfig.system_prompt)
      : agentConfig.systemPrompt
        ? String(agentConfig.systemPrompt)
        : undefined,
    model: agentConfig.llm_model
      ? String(agentConfig.llm_model)
      : agentConfig.model
        ? String(agentConfig.model)
        : undefined,
    temperature:
      typeof settings.temperature === 'number'
        ? settings.temperature
        : typeof agentConfig.temperature === 'number'
          ? agentConfig.temperature
          : undefined,
    fleet: fleetAgents,
    agentType: agentConfig.agent_type
      ? String(agentConfig.agent_type)
      : agentConfig.agentType
        ? String(agentConfig.agentType)
        : undefined,
    orchestrationMode: orch.mode ? String(orch.mode) : undefined,
  };
}

function configsDiffer(
  current: AgentConfig,
  fleetNames: string[],
  proposed: ProposedConfig,
): boolean {
  if (
    proposed.systemPrompt != null &&
    proposed.systemPrompt !== current.systemPrompt
  ) {
    return true;
  }
  if (proposed.model != null && proposed.model !== current.model) return true;
  if (
    proposed.temperature != null &&
    proposed.temperature !== current.temperature
  ) {
    return true;
  }
  if (proposed.fleet) {
    const a = [...proposed.fleet].sort().join(',');
    const b = [...fleetNames].sort().join(',');
    if (a !== b) return true;
  }
  return false;
}

export interface ConfigureTabProps {
  agent: AgentDetail;
  catalog: Agent[];
  readOnly: boolean;
}

const ConfigureTab: React.FC<ConfigureTabProps> = ({
  agent,
  catalog,
  readOnly,
}) => {
  const navigate = useNavigate();
  const management = isManagementAgent(agent);
  const [config, setConfig] = useState<AgentConfig>({
    systemPrompt: agent.config?.systemPrompt || '',
    model: agent.config?.model || agent.llmModel || 'gpt-4o',
    temperature: agent.config?.temperature ?? 0.7,
  });
  const [fleetNames, setFleetNames] = useState<string[]>(
    agent.fleet?.agents?.map((m) => m.agentName) || [],
  );
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'greeting',
      role: 'assistant',
      content: readOnly
        ? `I'm the configuration assistant for ${agent.name}. This agent is read-only — I can explain settings but cannot apply changes.`
        : `I'm the configuration assistant for ${agent.name}. Ask me to adjust fleet membership, orchestration, prompts, or model settings. I will never ask for API secrets.`,
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pending, setPending] = useState<ProposedConfig | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [updateConfig] = useUpdateAgentConfigMutation();
  const [publishAgent] = usePublishAgentMutation();
  const {
    subscribe,
    unsubscribe,
    addListener,
    removeListener,
  } = useWebSocketContext();

  const showPending = isSending || awaitingReply;

  useEffect(() => {
    setConfig({
      systemPrompt: agent.config?.systemPrompt || '',
      model: agent.config?.model || agent.llmModel || 'gpt-4o',
      temperature: agent.config?.temperature ?? 0.7,
    });
    setFleetNames(agent.fleet?.agents?.map((m) => m.agentName) || []);
  }, [agent.id, agent.config, agent.fleet, agent.llmModel]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, awaitingReply]);

  useEffect(() => {
    if (!sessionId) return;
    subscribe(sessionId, BUILDER_AGENT);
    return () => unsubscribe();
  }, [sessionId, subscribe, unsubscribe]);

  const configRef = useRef(config);
  const fleetRef = useRef(fleetNames);
  configRef.current = config;
  fleetRef.current = fleetNames;

  const applyAssistantText = useCallback(
    (content: string) => {
      const parsed = parseBuilderResponse(content);
      const message =
        parsed.message ||
        (parsed.agentConfig
          ? 'Proposed configuration updates — review and Accept or Discard.'
          : content);
      setMessages((prev) => {
        if (prev.some((m) => m.content === message && m.role === 'assistant')) {
          return prev;
        }
        return [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: message },
        ];
      });
      if (parsed.suggestions?.length) {
        setSuggestions(parsed.suggestions);
      }
      if (parsed.agentConfig && !readOnly) {
        const proposed = toProposed(parsed.agentConfig);
        if (
          configsDiffer(configRef.current, fleetRef.current, proposed)
        ) {
          setPending(proposed);
        }
      }
    },
    [readOnly],
  );

  useEffect(() => {
    const listener = (event: ExchangeEvent) => {
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        setAwaitingReply(false);
        applyAssistantText(content);
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: String(
              event.data?.error ||
                event.data?.message ||
                'Configuration assistant failed.',
            ),
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, removeListener, applyAssistantText]);

  const seedChips = useMemo(() => {
    if (readOnly) return [] as string[];
    const chips = ['Change model', 'Update system prompt'];
    if (management) {
      chips.unshift('Add fleet member');
      if (fleetNames.length) {
        chips.unshift(`Remove ${fleetNames[fleetNames.length - 1]} from fleet`);
      }
    }
    return chips;
  }, [readOnly, management, fleetNames]);

  const displayChips = suggestions.length ? suggestions : seedChips;

  const preview = useMemo(() => {
    return {
      systemPrompt: pending?.systemPrompt ?? config.systemPrompt,
      model: pending?.model ?? config.model,
      temperature: pending?.temperature ?? config.temperature,
      fleet: pending?.fleet ?? fleetNames,
      agentType: pending?.agentType ?? agent.agentType,
      orchestrationMode:
        pending?.orchestrationMode ?? agent.fleet?.orchestrationMode,
    };
  }, [pending, config, fleetNames, agent.agentType, agent.fleet]);

  const promptContext = useMemo(
    () => ({
      mode: 'configure',
      edit_mode: true,
      read_only: readOnly,
      target_agent: {
        id: agent.id,
        agent_name: agent.agentName,
        name: agent.name,
        agent_type: agent.agentType,
      },
      current_config: {
        system_prompt: config.systemPrompt,
        llm_model: config.model,
        settings: { temperature: config.temperature },
        agent_type: agent.agentType,
        fleet: { agents: fleetNames },
        orchestration: {
          mode: agent.fleet?.orchestrationMode || 'fan_out',
        },
      },
      catalog_agents: catalog
        .filter((a) => a.id !== agent.id)
        .slice(0, 40)
        .map((a) => ({
          agent_name: a.agentName,
          name: a.name,
          agent_type: a.agentType,
        })),
    }),
    [agent, catalog, config, fleetNames, readOnly],
  );

  const sendQuery = async (query: string) => {
    const text = query.trim();
    if (!text || showPending) return;
    setChatInput('');
    setActionError(null);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: text },
    ]);
    setAwaitingReply(true);
    try {
      const result = await sendMessage({
        query: text,
        agentName: BUILDER_AGENT,
        sessionId,
        metadata: {
          source: 'configure',
          prompt_context: promptContext,
        },
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, BUILDER_AGENT);
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) return;
      setAwaitingReply(false);
      if (replyText) applyAssistantText(replyText);
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const message =
        status === 404
          ? 'Agent Builder is not available in this environment.'
          : 'Unable to reach the configuration assistant.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', content: message },
      ]);
    }
  };

  const persist = async (
    next: AgentConfig,
    nextFleet: string[],
    extra?: Record<string, unknown>,
  ) => {
    await updateConfig({
      id: agent.id,
      config: {
        system_prompt: next.systemPrompt,
        model: next.model,
        temperature: next.temperature,
        ...(nextFleet.length || management
          ? {
              fleet: { agents: nextFleet },
              agent_type: 'management',
              orchestration: {
                mode:
                  (extra?.orchestrationMode as string) ||
                  agent.fleet?.orchestrationMode ||
                  'fan_out',
              },
            }
          : {}),
        ...extra,
      },
    }).unwrap();
  };

  const handleAccept = async () => {
    if (!pending || readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      const next: AgentConfig = {
        systemPrompt: pending.systemPrompt ?? config.systemPrompt,
        model: pending.model ?? config.model,
        temperature: pending.temperature ?? config.temperature,
      };
      const nextFleet = pending.fleet ?? fleetNames;
      await persist(next, nextFleet, {
        orchestrationMode: pending.orchestrationMode,
      });
      setConfig(next);
      setFleetNames(nextFleet);
      setPending(null);
      setMessages((prev) => [
        ...prev,
        {
          id: `ok-${Date.now()}`,
          role: 'assistant',
          content:
            'Applied to Draft. Publish when you are ready to make this live.',
        },
      ]);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to apply proposal',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setPending(null);

  const handleFleetChange = async (names: string[]) => {
    setFleetNames(names);
    if (readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      await persist(config, names);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to update fleet',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      await persist(config, fleetNames);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to save draft',
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      await persist(config, fleetNames);
      await publishAgent(agent.id).unwrap();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to publish agent',
      );
    } finally {
      setSaving(false);
    }
  };

  const openAgent = (idOrName: string) => {
    navigate(`/agents/${encodeURIComponent(idOrName)}`);
  };

  const fleetDiffLines = useMemo(() => {
    if (!pending?.fleet) return [];
    const current = new Set(fleetNames);
    const next = new Set(pending.fleet);
    const removed = fleetNames.filter((n) => !next.has(n));
    const added = pending.fleet.filter((n) => !current.has(n));
    return [
      ...removed.map((n) => `− ${n}`),
      ...added.map((n) => `+ ${n}`),
    ];
  }, [pending, fleetNames]);

  return (
    <Layout>
      <EditorPane>
        <ChatShell>
          <ChatHeader>
            <ChatTitle>Agent Builder</ChatTitle>
            <ChatSubtitle>
              Describe changes in natural language. AI proposes diffs — you
              accept or discard.
            </ChatSubtitle>
          </ChatHeader>
          <MessageList>
            {messages.map((m) => (
              <Bubble key={m.id} $role={m.role}>
                {m.content}
              </Bubble>
            ))}
            {showPending && (
              <PendingBubble role="status">Thinking…</PendingBubble>
            )}
            <div ref={endRef} />
          </MessageList>
          <ChatInputRow
            onSubmit={(e) => {
              e.preventDefault();
              void sendQuery(chatInput);
            }}
          >
            <ChatInput
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask to configure your agent…"
              disabled={showPending}
              rows={1}
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!chatInput.trim() || showPending}
            >
              Send
            </Button>
          </ChatInputRow>
        </ChatShell>

        {displayChips.length > 0 && (
          <ChipRow>
            {displayChips.map((chip) => (
              <Chip
                key={chip}
                type="button"
                disabled={showPending}
                onClick={() => void sendQuery(chip)}
              >
                {chip}
              </Chip>
            ))}
          </ChipRow>
        )}

        {pending && (
          <DiffCard>
            <strong>Proposed configuration change</strong>
            <div style={{ marginTop: '0.5rem' }}>
              {pending.model != null && pending.model !== config.model && (
                <div>
                  Model: {config.model} → {pending.model}
                </div>
              )}
              {pending.temperature != null &&
                pending.temperature !== config.temperature && (
                  <div>
                    Temperature: {config.temperature} → {pending.temperature}
                  </div>
                )}
              {pending.systemPrompt != null &&
                pending.systemPrompt !== config.systemPrompt && (
                  <div>System prompt updated</div>
                )}
              {fleetDiffLines.length > 0 && (
                <div
                  style={{
                    marginTop: '0.35rem',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {fleetDiffLines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  <div>({pending.fleet?.length ?? 0} specialists)</div>
                </div>
              )}
            </div>
            <DiffActions>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAccept}
                disabled={saving || readOnly}
              >
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscard}
                disabled={saving}
              >
                Discard
              </Button>
            </DiffActions>
          </DiffCard>
        )}

        {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

        <ManualToggle
          type="button"
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? 'Hide manual editor ▴' : 'Edit manually ▾'}
        </ManualToggle>

        {showManual && (
          <>
            {(management || fleetNames.length > 0) && (
              <ConfigureFleetPanel
                fleet={{
                  agents: fleetNames.map((name) => {
                    const existing = agent.fleet?.agents.find(
                      (m) => m.agentName === name,
                    );
                    return (
                      existing || {
                        agentName: name,
                        displayName: name,
                      }
                    );
                  }),
                  orchestrationMode: agent.fleet?.orchestrationMode,
                  orchestrationLabel: agent.fleet?.orchestrationLabel,
                }}
                catalog={catalog.filter((a) => a.id !== agent.id)}
                readOnly={readOnly}
                onChange={handleFleetChange}
                onOpenAgent={openAgent}
              />
            )}
            {agent.membership && !management && (
              <ConfigureMembershipPanel
                membership={agent.membership}
                onOpenParent={openAgent}
              />
            )}
            {!readOnly && (
              <ConfigActions>
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Draft'}
                </Button>
                <Button
                  variant="primary"
                  onClick={handlePublish}
                  disabled={saving}
                >
                  Publish
                </Button>
              </ConfigActions>
            )}
          </>
        )}
      </EditorPane>

      <PreviewCard padding={20} elevation="sm">
        <Text variant="h4" style={{ margin: 0 }}>
          Live Configuration
        </Text>
        {(management || preview.fleet.length > 0) && (
          <div>
            <BlockHeader>Fleet & orchestration</BlockHeader>
            <FieldRow>
              <FieldName>mode</FieldName>
              <FieldValue
                $changed={
                  pending?.orchestrationMode != null &&
                  pending.orchestrationMode !== agent.fleet?.orchestrationMode
                }
              >
                {preview.orchestrationMode || '—'}
              </FieldValue>
            </FieldRow>
            <FieldRow>
              <FieldName>members</FieldName>
              <FieldValue $changed={Boolean(pending?.fleet)}>
                {preview.fleet.length
                  ? preview.fleet.join(', ')
                  : 'None'}
              </FieldValue>
            </FieldRow>
          </div>
        )}
        <div>
          <BlockHeader>Model</BlockHeader>
          <FieldRow>
            <FieldName>llm_model</FieldName>
            <FieldValue
              $changed={
                pending?.model != null && pending.model !== config.model
              }
            >
              {preview.model}
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldName>temperature</FieldName>
            <FieldValue
              $changed={
                pending?.temperature != null &&
                pending.temperature !== config.temperature
              }
            >
              {preview.temperature}
            </FieldValue>
          </FieldRow>
        </div>
        <div>
          <BlockHeader>System prompt</BlockHeader>
          <PromptPreview>
            {preview.systemPrompt || '—'}
          </PromptPreview>
        </div>
        {pending && (
          <Badge variant="warning" size="sm">
            Pending changes (Accept to apply to Draft)
          </Badge>
        )}
        <Text
          variant="body1"
          style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}
        >
          Empty fleet is warned on Publish. Changes stay on Draft until you
          publish.
        </Text>
      </PreviewCard>
    </Layout>
  );
};

export default ConfigureTab;
export { parseBuilderResponse, toProposed, configsDiffer };
